import React, { FC } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Routes, routesConfigs } from '../routes';
import { transition } from '../theme';
import { getAppViewRelevantIsDrawerOpen } from '../app/store/appViewSlice';
import { connect } from 'react-redux';
import Toggle from './Toggle';
import { RootState } from '../store';
import { Dispatch } from 'redux';
import { routingOnRouteRequest } from 'src/app/store/routingSlice';
import { getIsSelectedRoute } from './utils';

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const AppDrawer: FC<Props> = ({ isOpen, goTo }) => {
  const location = useLocation();
  const theme = useTheme();

  const onGoToLink = (path: Routes) => {
    goTo(path);
  };

  return (
    <Drawer
      open={isOpen}
      variant="permanent"
      anchor="right"
      elevation={0}
      SlideProps={{
        direction: 'left',
      }}
      sx={{
        transition: transition({ duration: 0.4 }),
        width: ({ sizes }) =>
          isOpen ? sizes.general.drawer.open : sizes.general.drawer.closed,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          overflowY: 'visible',
          width: ({ sizes }) =>
            isOpen ? sizes.general.drawer.open : sizes.general.drawer.closed,
          transition: transition({ duration: 0.4 }),
          boxSizing: 'border-box',
          background: ({ palette }) => palette.paperGrey,
        },
      }}
    >
      <Toolbar />
      <Box height="100%" sx={{ position: 'relative' }}>
        <List
          component="ul"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflowX: 'hidden',
          }}
        >
          {routesConfigs.map(({ route, Icon, name }) => {
            const isSelected = getIsSelectedRoute(
              route,
              location.pathname as Routes,
            );
            const itemColor = isSelected
              ? theme.palette.primary.light
              : theme.palette.common.black;

            return (
              <Tooltip
                key={route}
                placement="left"
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 10], // [skidding, distance]
                      },
                    },
                  ],
                }}
                enterDelay={350}
                enterNextDelay={350}
                title={name}
                disableHoverListener={isOpen}
                disableFocusListener={isOpen}
                disableTouchListener={isOpen}
                disableInteractive
              >
                <ListItemButton
                  onClick={() => onGoToLink(route)}
                  selected={isSelected}
                  sx={{
                    padding: 0,
                    transition: transition(),
                    mx: 1.875,
                    px: 1,
                    borderRadius: ({ spacing }) => spacing(0.625),
                    height: ({ spacing }) => spacing(3.75),
                    '&:hover': {
                      bgcolor: ({ palette }) =>
                        alpha(palette.primary.light, 0.25),
                    },
                  }}
                  key={route}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: ({ spacing }) => spacing(5),
                      color: itemColor,
                      transition: transition(),
                      opacity: 0.8,
                    }}
                  >
                    <Icon sx={{ fontSize: ({ spacing }) => spacing(2.5) }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      align: 'right',
                      fontWeight: 600,
                      sx: {
                        color: itemColor,
                        transition: transition({ duration: 0.2 }),
                        opacity: Number(isOpen),
                        textWrap: 'nowrap',
                      },
                    }}
                    primary={name}
                  />
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
        <Toggle />
      </Box>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOpen: getAppViewRelevantIsDrawerOpen(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTo: (path: Routes) => dispatch(routingOnRouteRequest({ path })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AppDrawer);
